import React, { useEffect, useState } from "react";
import { Spin, message as antdMessage } from "antd";
import { Loader2 } from "lucide-react";

import CompanyInformationSidebar from "./CompanyInformationSidebar";
import CompanyCreateModal from "./CreateCompanyModal";
import CompanyInformationForm from "./CompanyInformationForm";

import { CompanyInfo } from "../../../../bitmaker/schemas/Billing/CompanyInfo";

import { useCompanies } from "../../../../hooks/useCompanies";
import { useApiError } from "../../../../hooks/useApiError";

import { BitmakerAPIClient } from "../../../../services/BitmakerApp";

export default function CompanyInformationManagement() {
  const { companies, loadingCompanies, fetchCompaniesError, refetchCompanies } =
    useCompanies();

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [createCompanyError, setCreateCompanyError] = useState<string | null>(
    null,
  );
  const [updateCompanyError, setUpdateCompanyError] = useState<string | null>(
    null,
  );
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [isUpdatingCompany, setIsUpdatingCompany] = useState(false);

  const { handleAPIError } = useApiError();

  // Auto-select first company after load
  useEffect(() => {
    if (companies.length === 0) {
      setSelectedCompanyId(null);
      return;
    }

    if (!selectedCompanyId) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  const selectedCompany =
    companies.find((c) => c.id === selectedCompanyId) || null;

  const handleCancelCreateModal = () => {
    setCreateCompanyError(null);
    setIsCreateModalOpen(false);
  };

  const handleCreateCompany = (data: {
    companyName: string;
    corporateName?: string;
    country?: string;
  }) => {
    if (isCreatingCompany) return;

    setCreateCompanyError(null);
    setIsCreatingCompany(true);

    BitmakerAPIClient.post(
      "/billing/settings/company-info",
      data,
      (response) => {
        const { isError, message: apiMessage } = handleAPIError(response);

        if (isError) {
          console.error("Error creating company:", apiMessage);
          setCreateCompanyError("Error creating company. Please try again.");
          setIsCreatingCompany(false);
          return;
        }

        const newCompany = response.data;

        antdMessage.success("Company created successfully!");

        setIsCreatingCompany(false);
        setIsCreateModalOpen(false);
        setSelectedCompanyId(newCompany.id);
        refetchCompanies();
      },
    );
  };

  const handleUpdateCompany = (updatedCompany: CompanyInfo) => {
    if (isUpdatingCompany || !selectedCompanyId) return;

    setUpdateCompanyError(null);
    setIsUpdatingCompany(true);

    BitmakerAPIClient.put(
      `/billing/settings/company-info/${selectedCompanyId}`,
      updatedCompany,
      (response) => {
        const { isError, message: apiMessage } = handleAPIError(response);

        if (isError) {
          console.error("Error updating company:", apiMessage);
          setUpdateCompanyError("Error updating company. Please try again.");
          setIsUpdatingCompany(false);
          return;
        }

        antdMessage.success("Company updated successfully!");

        setIsUpdatingCompany(false);
        refetchCompanies();
      },
    );
  };

  if (fetchCompaniesError) {
    return (
      <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
        {fetchCompaniesError}
      </div>
    );
  }

  if (loadingCompanies) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin
          indicator={<Loader2 className="animate-spin" />}
          tip="Loading company information..."
        />
      </div>
    );
  }

  return (
    <>
      <div className="w-full h-full flex gap-8 bg-gray-50 p-6 rounded-2xl">
        {/* Sidebar */}
        <CompanyInformationSidebar
          companies={companies}
          selectedCompanyId={selectedCompanyId}
          onSelectCompany={setSelectedCompanyId}
          onAddCompany={() => setIsCreateModalOpen(true)}
        />

        {/* Main Form */}
        <div className="flex-1 bg-white rounded-2xl shadow-md p-6">
          {selectedCompany ? (
            <CompanyInformationForm
              company={selectedCompany}
              onSave={handleUpdateCompany}
              loading={isUpdatingCompany}
              submitError={updateCompanyError}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              No company selected.
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <CompanyCreateModal
        open={isCreateModalOpen}
        onCancel={handleCancelCreateModal}
        onCreate={handleCreateCompany}
        loading={isCreatingCompany}
        createError={createCompanyError}
      />
    </>
  );
}
